// RED Mode (formerly bug-report mode, Phase 1.5): a sticky, admin-only point-and-click work-order
// generation mode — toggled from General Settings — that frames the viewport, lets the admin pin
// multiple elements (⌥-click, or the tray's one-shot picker), classify each as a Requirement /
// Enhancement / Defect, and submit the session as ONE work order or as individual r/d/e items.
// Device-local by design: this is an operator mode, not published site config. Annotations persist
// in localStorage so an accidental reload keeps the tray (element markers don't survive reloads —
// the context snapshots do). Internal names stay `bugMode`/`BugReviewMode` from the Phase 1.5 origin.
import { useSyncExternalStore } from "react";
import type { ElementContext } from "./bugPicker";

/** RED = Requirements · Enhancements · Defects. */
export type RedType = "requirement" | "enhancement" | "defect";
const RED_TYPES: RedType[] = ["requirement", "enhancement", "defect"];

export interface BugAnnotation {
  id: string;
  type: RedType;
  msg: string;
  context: ElementContext;
}

interface BugModeState {
  on: boolean;
  annotations: BugAnnotation[];
}

const KEY = "ir.bugmode.v1";
/** Server stores at most this many annotations per batch — enforced here too so the tray never
 *  lies about what was submitted. */
export const BATCH_CAP = 20;

function load(): BugModeState {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? "") as Partial<BugModeState>;
    // Validate every persisted entry — one malformed record (schema drift, interrupted write)
    // must not white-screen the app on every load while the mode is on.
    const anns = (Array.isArray(raw.annotations) ? raw.annotations : [])
      .filter(
        (a): a is BugAnnotation =>
          typeof (a as BugAnnotation)?.id === "string" &&
          typeof (a as BugAnnotation)?.msg === "string" &&
          typeof (a as BugAnnotation)?.context === "object" &&
          (a as BugAnnotation).context !== null,
      )
      // pre-RED sessions had no `type` — default to defect (the bug-report heritage).
      .map((a) => ({
        ...a,
        type: RED_TYPES.includes(a.type) ? a.type : ("defect" as RedType),
      }));
    return { on: Boolean(raw.on), annotations: anns };
  } catch {
    return { on: false, annotations: [] };
  }
}

let state: BugModeState = load();
const subs = new Set<() => void>();

function emit(next: BugModeState) {
  state = next;
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    // storage may be unavailable — mode still works for this page life
  }
  subs.forEach((fn) => fn());
}

export function useBugMode(): BugModeState {
  return useSyncExternalStore(
    (fn) => {
      subs.add(fn);
      return () => subs.delete(fn);
    },
    () => state,
  );
}

export const bugMode = {
  toggle(on: boolean) {
    emit({ ...state, on });
  },
  pinContext(context: ElementContext, msg = "", type: RedType = "defect"): boolean {
    if (state.annotations.length >= BATCH_CAP) return false;
    const id = `${Date.now().toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`;
    emit({ ...state, annotations: [...state.annotations, { id, type, msg, context }] });
    return true;
  },
  setType(id: string, type: RedType) {
    emit({
      ...state,
      annotations: state.annotations.map((a) => (a.id === id ? { ...a, type } : a)),
    });
  },
  setMsg(id: string, msg: string) {
    emit({
      ...state,
      annotations: state.annotations.map((a) => (a.id === id ? { ...a, msg } : a)),
    });
  },
  remove(id: string) {
    emit({ ...state, annotations: state.annotations.filter((a) => a.id !== id) });
  },
  clear() {
    emit({ ...state, annotations: [] });
  },
  /** Remove only the given ids — used after submit so a pin added mid-flight isn't lost. */
  clearIds(ids: string[]) {
    const set = new Set(ids);
    emit({ ...state, annotations: state.annotations.filter((a) => !set.has(a.id)) });
  },
};
